import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, BarChart3, BookOpenCheck, ClipboardList, ExternalLink, Loader2, RefreshCcw, Search, Target, Users } from 'lucide-react'
import DashboardSidebar from '../components/DashboardSidebar'
import { courseApi, type ClassInfo } from '../api/lessonBuilderApi'
import { learningApi, type StudentInterventionRow, type TeacherClassDashboard } from '../api/learningApi'

const levelText: Record<string, string> = {
  weak: 'Yếu',
  at_risk: 'Cần củng cố',
  pass: 'Đạt',
  good: 'Khá',
  excellent: 'Giỏi',
  linked_no_data: 'Chưa có dữ liệu',
  no_data: 'Chưa liên kết',
  unknown: 'Chưa rõ',
}

const levelClass = (level?: string) => {
  if (level === 'weak') return 'bg-rose-50 text-rose-700 border-rose-100'
  if (level === 'at_risk') return 'bg-amber-50 text-amber-700 border-amber-100'
  if (level === 'pass') return 'bg-blue-50 text-blue-700 border-blue-100'
  if (level === 'good' || level === 'excellent') return 'bg-emerald-50 text-emerald-700 border-emerald-100'
  return 'bg-slate-50 text-slate-500 border-slate-100'
}

const primaryAction = (row: StudentInterventionRow) => {
  const rec = row.recommendations?.[0]
  if (!rec) return 'Chưa đủ dữ liệu để khuyến nghị.'
  return rec.actions?.[0] || rec.reason || 'Theo dõi thêm kết quả học tập.'
}

const buildStudySuggestions = (data: TeacherClassDashboard | null) => {
  if (!data) return []
  const weakStudents = (data.students || []).filter((s) => s.status === 'weak' || s.status === 'at_risk')
  const suggestions: Array<{ title: string; desc: string; tone: 'rose' | 'amber' | 'blue' }> = []
  if (weakStudents.length) {
    suggestions.push({
      title: `Ưu tiên ${weakStudents.length} học sinh yếu/cần củng cố`,
      desc: 'Giao bài ôn ngắn theo đúng chủ đề yếu, sau đó kiểm tra lại bằng quiz hoặc đề PMaker mức cơ bản.',
      tone: 'rose',
    })
  }
  const topTopic = data.weak_topics?.[0]
  if (topTopic) {
    suggestions.push({
      title: `Dạy lại chủ đề: ${topTopic.topic_title || topTopic.topic_code}`,
      desc: `${topTopic.students} học sinh đang yếu ở chủ đề này. Nên gửi lại bài ebook, video ôn và 5-10 câu luyện tập cùng chủ đề.`,
      tone: 'amber',
    })
  }
  const topQuestion = data.question_issues?.[0]
  if (topQuestion) {
    suggestions.push({
      title: 'Phân tích câu sai nhiều',
      desc: `${topQuestion.wrong_count} lượt sai ở một câu hỏi. Nên dùng câu này để chữa chung, chỉ ra lỗi tư duy và giao câu tương tự.`,
      tone: 'blue',
    })
  }
  if (!suggestions.length) {
    suggestions.push({
      title: 'Chưa đủ dữ liệu để can thiệp tự động',
      desc: 'Cần học sinh đăng nhập đúng email, học bài/làm quiz/nộp bài hoặc làm đề PMaker để hệ thống sinh khuyến nghị chính xác.',
      tone: 'blue',
    })
  }
  return suggestions
}

const LearningInterventionPage: React.FC = () => {
  const [classes, setClasses] = useState<ClassInfo[]>([])
  const [className, setClassName] = useState('')
  const [data, setData] = useState<TeacherClassDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')

  const load = async (name = className) => {
    setLoading(true)
    setError('')
    try {
      const dash = await learningApi.teacherClassDashboard(name || undefined)
      setData(dash)
    } catch (e) {
      setError((e as Error)?.message || 'Không tải được dữ liệu can thiệp.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    document.title = 'Can thiệp học tập | VatLi365'
    courseApi.myClasses().then((cs) => {
      setClasses(cs)
      const first = cs[0]?.name || ''
      setClassName(first)
      load(first)
    }).catch(() => load(''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const students = useMemo(() => {
    const list = data?.students || []
    const keyword = q.trim().toLowerCase()
    const filtered = keyword
      ? list.filter((s) => `${s.student_name || ''} ${s.student_email || ''} ${s.class_name || ''}`.toLowerCase().includes(keyword))
      : list
    return [...filtered].sort((a, b) => {
      const riskA = a.status === 'weak' ? 0 : a.status === 'at_risk' ? 1 : a.weak_count ? 2 : 3
      const riskB = b.status === 'weak' ? 0 : b.status === 'at_risk' ? 1 : b.weak_count ? 2 : 3
      return riskA - riskB || (a.avg_percent ?? 101) - (b.avg_percent ?? 101)
    })
  }, [data, q])

  const studySuggestions = useMemo(() => buildStudySuggestions(data), [data])

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <DashboardSidebar />
      <main className="flex-1 p-6">
        <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-500">E-learning Studio / Cá nhân hoá</p>
            <h1 className="mt-1 flex items-center gap-2 text-2xl font-black text-slate-900">
              <Target className="text-rose-600" size={24} /> Can thiệp học tập
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              Phát hiện học sinh yếu theo chủ đề, câu hỏi sai nhiều và gợi ý hành động tiếp theo từ dữ liệu Ebook, PMaker, quiz và bài tự luận.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={className}
              onChange={(e) => { setClassName(e.target.value); load(e.target.value) }}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="">Tất cả lớp</option>
              {classes.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.linked}/{c.total})</option>)}
            </select>
            <button onClick={() => load()} className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50">
              <RefreshCcw size={16} /> Tải lại
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        )}

        <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-4">
          <StatCard icon={Users} label="Học sinh" value={data?.summary.students ?? 0} />
          <StatCard icon={BookOpenCheck} label="Đã liên kết tài khoản" value={data?.summary.linked ?? 0} />
          <StatCard icon={BarChart3} label="Hoạt động học tập" value={data?.summary.events ?? 0} />
          <StatCard icon={AlertTriangle} label="Yếu / cần củng cố" value={data?.summary.weak_or_at_risk ?? 0} tone="rose" />
        </div>

        <section className="mb-5 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <ClipboardList className="text-blue-600" size={18} />
            <h2 className="text-lg font-black text-slate-900">Gợi ý học tập</h2>
          </div>
          {loading ? <LoadingLine /> : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {studySuggestions.map((s) => (
                <div
                  key={s.title}
                  className={`rounded-2xl border p-4 ${
                    s.tone === 'rose'
                      ? 'border-rose-100 bg-rose-50/60'
                      : s.tone === 'amber'
                        ? 'border-amber-100 bg-amber-50/60'
                        : 'border-blue-100 bg-blue-50/60'
                  }`}
                >
                  <p className="font-black text-slate-900">{s.title}</p>
                  <p className="mt-1 text-sm text-slate-600">{s.desc}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-lg font-black text-slate-900">Chủ đề cần dạy lại</h2>
            {loading ? <LoadingLine /> : !data?.weak_topics.length ? (
              <p className="text-sm text-slate-400">Chưa phát hiện chủ đề yếu từ dữ liệu hiện có.</p>
            ) : (
              <div className="space-y-2">
                {data.weak_topics.slice(0, 8).map((t) => (
                  <div key={t.topic_code} className="flex items-center justify-between rounded-xl border border-amber-100 bg-amber-50/40 px-3 py-2">
                    <div>
                      <p className="font-bold text-slate-800">{t.topic_title || t.topic_code}</p>
                      <p className="text-xs text-slate-400">{t.topic_code}</p>
                    </div>
                    <span className="rounded-lg bg-white px-2 py-1 text-xs font-black text-amber-700">{t.students} HS</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-lg font-black text-slate-900">Câu hỏi sai nhiều</h2>
            {loading ? <LoadingLine /> : !data?.question_issues.length ? (
              <p className="text-sm text-slate-400">Chưa có dữ liệu câu sai đủ rõ.</p>
            ) : (
              <div className="space-y-2">
                {data.question_issues.slice(0, 6).map((it) => (
                  <div key={it.question_id} className="rounded-xl border border-slate-100 px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-slate-800 line-clamp-1">{it.question || it.question_id}</p>
                      <span className="shrink-0 rounded-lg bg-rose-50 px-2 py-1 text-xs font-black text-rose-700">{it.wrong_count} sai</span>
                    </div>
                    {it.resource_title && <p className="mt-1 text-xs text-slate-400">{it.resource_title}</p>}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="text-lg font-black text-slate-900">Danh sách học sinh cần theo dõi</h2>
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
              <Search size={16} className="text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm học sinh/email..." className="w-64 bg-transparent text-sm outline-none" />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-300" size={28} /></div>
          ) : students.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">
              Chưa có học sinh hoặc chưa liên kết được tài khoản học sinh bằng email.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Học sinh</th>
                    <th className="px-4 py-3">Trạng thái</th>
                    <th className="px-4 py-3">TB</th>
                    <th className="px-4 py-3">Điểm yếu</th>
                    <th className="px-4 py-3">Khuyến nghị chính</th>
                    <th className="px-4 py-3 text-right">Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, idx) => (
                    <tr key={`${s.student_id || s.student_email || idx}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-900">{s.student_name || s.student_email || 'Học sinh'}</p>
                        <p className="text-xs text-slate-400">{s.student_email || s.class_name || ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-black ${levelClass(s.status)}`}>
                          {levelText[s.status] || s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-black text-slate-800">{s.avg_percent == null ? '—' : `${Math.round(s.avg_percent)}%`}</td>
                      <td className="px-4 py-3 text-rose-700 font-bold">{s.weak_count}</td>
                      <td className="px-4 py-3 text-slate-600">{primaryAction(s)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Link to="/teacher/pmaker" className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700 hover:bg-blue-100">
                            Gợi ý đề PMaker
                          </Link>
                          <a href="https://pmaker.vatli365.vn/classes" target="_blank" rel="noreferrer" className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-100">
                            Giao bài <ExternalLink size={12} className="inline" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}

const LoadingLine = () => (
  <div className="flex items-center gap-2 py-6 text-sm font-semibold text-slate-400">
    <Loader2 className="animate-spin" size={18} /> Đang phân tích dữ liệu...
  </div>
)

const StatCard: React.FC<{ icon: React.ElementType; label: string; value: number | string; tone?: 'rose' }> = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-xs font-black uppercase tracking-wide text-slate-400">{label}</p>
      <Icon size={18} className={tone === 'rose' ? 'text-rose-500' : 'text-blue-500'} />
    </div>
    <p className="mt-2 text-2xl font-black text-slate-900">{value}</p>
  </div>
)

export default LearningInterventionPage
